import React, { useMemo, useRef } from 'react';
import imageUrlBuilder from '@sanity/image-url';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import locationLabelText from '@tmap/mmm-core/locationLabelText';
import { toKey, types } from '@tmap/mmm-core/entityKey';
import OfferValueItem from '../offers/offerValueItem';
import findImpressionFeature from '../../lib/findImpressionFeature';
import getPrimaryIncentiveImage from '../../lib/getPrimaryIncentiveImage';
import useImpressions, { ImpressionTracker } from '../../hooks/useImpressions';
import client from '../../client';
import LinkCard from '../linkCard';
import IncentiveCTAButton from './incentiveCTAButton';
import IncompleteTasksCTA from './incompleteTasksCTA';
import useActiveProgram from '../../hooks/useActiveProgram';

const builder = imageUrlBuilder(client);

const incentiveImgStyles = () => ({
  width: '100%',
  maxHeight: '550px',
  objectFit: 'cover',
  borderRadius: '8px',
});
const StyledIncentivePicture = styled('picture')(incentiveImgStyles);
const StyledIncentiveImg = styled('img')(incentiveImgStyles);

const SpacedGrid = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const OfferGrid = styled(Grid)(() => ({
  height: '100%',
  padding: '0 24px 24px 24px',
  marginTop: 0,
}));

const ButtonWrapper = styled('div')(() => ({
  position: 'absolute',
  paddingRight: '24px',
  width: '100%',
  bottom: 0,
}));

function IncentiveCardHorizontal(props) {
  const {
    incentive,
    shouldLazyLoad,
    lazyProps,
    placementId,
    pageId,
    ...cardProps
  } = props;
  const primaryImage = getPrimaryIncentiveImage(incentive);
  const altText = `Get paid to live in ${locationLabelText(incentive.locationText, incentive.regionText)}`;

  let valuesToDisplay = incentive.values;
  const valueCount = (incentive.values || []).length;
  let additionalOffer;

  if (valueCount > 4) {
    valuesToDisplay = incentive.values.slice(0, 3);
    additionalOffer = {
      valueDescription: `${valueCount - valuesToDisplay.length} other incentives`,
    };
  }

  const impressionMeta = useMemo(() => ({ pageId }), [pageId]);
  const { trackView, trackClick } = useImpressions(
    toKey(types.INCENTIVE, incentive._id),
    placementId,
    impressionMeta,
  );

  const baseElement = useRef();
  const applyTarget = useRef();

  const handleImpressionClick = (event) => {
    const elementsToTrack = new Map([
      [applyTarget.current, 'apply'],
      [baseElement.current, 'base'],
    ]);

    const featureName = findImpressionFeature(event, elementsToTrack);
    trackClick({ feature: featureName });
  };

  const program = useActiveProgram(incentive);

  return (
    <ImpressionTracker
      trackView={trackView}
      onClickCapture={handleImpressionClick}
      ref={baseElement}
    >
      <LinkCard
        href={`/get-paid/${encodeURIComponent(incentive.slug.current)}`}
        alt={incentive.title || 'View Program details'}
        elevation={1}
        sx={{ height: '100%', borderRadius: '8px' }}
        {...cardProps}
      >
        <OfferGrid container spacing={3} justifyContent='space-between' alignItems='stretch'>
          <Grid item xs={12} sm={6}>
            <StyledIncentivePicture>
              <source srcSet={builder.image(primaryImage).format('webp').width(500).url()} width={500} media='(min-width: 1200px)' />
              <StyledIncentiveImg
                src={builder.image(primaryImage).width(330).url()}
                width={330}
                height='100%'
                alt={altText}
              />
            </StyledIncentivePicture>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ position: 'relative' }}>
            <SpacedGrid container spacing={1}>
              <Grid item xs>
                <Typography variant='h6'>{incentive.title || 'Total Value'}</Typography>
              </Grid>
              <Grid item text='right'>
                <Typography variant='h6' color='secondary'>
                  {incentive.totalValue ? `$${new Intl.NumberFormat().format(incentive.totalValue)}` : ''}
                </Typography>
              </Grid>
            </SpacedGrid>
            <SpacedGrid container spacing={1} sx={{ marginBottom: '48px' }}>
              {valuesToDisplay?.length > 0
                && valuesToDisplay.map((incentiveValue, i) => (
                  <Grid item xs={12} key={`iv-${i}`}>
                    <OfferValueItem offerValue={incentiveValue} disableLinks />
                  </Grid>
                ))}
              {additionalOffer && (
                <Grid item xs={12}>
                  <OfferValueItem offerValue={additionalOffer} disableLinks />
                </Grid>
              )}
              {program?.incompleteTasks > 0 && (
                <Grid item xs={12}>
                  <IncompleteTasksCTA incentive={incentive} />
                </Grid>
              )}
            </SpacedGrid>
            <ButtonWrapper>
              <IncentiveCTAButton
                ref={applyTarget}
                incentive={incentive}
              />
            </ButtonWrapper>
          </Grid>
        </OfferGrid>
      </LinkCard>
    </ImpressionTracker>
  );
}

export default IncentiveCardHorizontal;
